/**
 * Custom hook for managing OAuth tokens
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { apiDelete, apiGet } from '@/lib/http/client';

export interface OAuthToken {
  id: number;
  application_name: string;
  scope: string;
  created: string;
  expires: string;
}

// Paginated response shape from DRF PageNumberPagination
interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

interface UseOAuthTokensOptions {
  orgId: number | undefined;
}

export function useOAuthTokens({ orgId }: UseOAuthTokensOptions) {
  const [tokens, setTokens] = useState<OAuthToken[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Track current orgId in a ref to detect stale closures
  const currentOrgIdRef = useRef<number | undefined>(orgId);

  // Keep the ref in sync with the prop
  useEffect(() => {
    currentOrgIdRef.current = orgId;
  }, [orgId]);

  const refreshTokens = useCallback(async () => {
    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // If orgId is undefined, clear state and abort
    if (!orgId) {
      setTokens([]);
      setError(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    const requestOrgId = orgId;

    try {
      setLoading(true);
      setError(null);

      // Fetch all pages of tokens (pagination returns page_size=50 by default)
      let allTokens: OAuthToken[] = [];
      let nextUrl: string | null = `/api/orgs/${requestOrgId}/oauth/tokens/`;

      while (nextUrl) {
        // Check for abort before each request
        if (
          controller.signal.aborted ||
          currentOrgIdRef.current !== requestOrgId
        ) {
          return;
        }

        const response: PaginatedResponse<OAuthToken> = await apiGet<
          PaginatedResponse<OAuthToken>
        >(nextUrl, {}, undefined, controller.signal);
        allTokens = [...allTokens, ...response.results];
        nextUrl = response.next;
      }

      // Only update state if this request wasn't aborted AND org hasn't changed
      if (
        !controller.signal.aborted &&
        currentOrgIdRef.current === requestOrgId
      ) {
        setTokens(allTokens);
      }
    } catch (err) {
      // Ignore AbortError - it's expected when orgId changes
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      console.error('Error loading OAuth tokens:', err);
      // Only set error if this request wasn't aborted AND org hasn't changed
      if (
        !controller.signal.aborted &&
        currentOrgIdRef.current === requestOrgId
      ) {
        setError("We couldn't load your OAuth tokens. Try again.");
      }
    } finally {
      // Only clear loading if this request wasn't aborted AND org hasn't changed
      if (
        !controller.signal.aborted &&
        currentOrgIdRef.current === requestOrgId
      ) {
        setLoading(false);
      }
    }
  }, [orgId]);

  const revokeToken = useCallback(
    async (tokenId: number) => {
      if (!orgId) {
        return;
      }

      if (
        !window.confirm(
          'Are you sure you want to revoke this OAuth token? This will immediately disconnect any MCP clients using this token.'
        )
      ) {
        return;
      }

      // Capture the orgId at the start of the request
      const requestOrgId = orgId;

      try {
        setError(null);
        await apiDelete(`/api/orgs/${requestOrgId}/oauth/tokens/${tokenId}/`);
        // Check if org changed during async operation using ref (not stale closure)
        if (currentOrgIdRef.current !== requestOrgId) {
          // Org changed during the request - discard results silently
          return;
        }
        await refreshTokens();
      } catch (err) {
        // Check for org change before setting error state
        if (currentOrgIdRef.current !== requestOrgId) {
          return;
        }
        console.error('Error revoking OAuth token:', err);
        setError("We couldn't revoke that OAuth token. Try again.");
        throw err;
      }
    },
    [orgId, refreshTokens]
  );

  useEffect(() => {
    refreshTokens();
  }, [refreshTokens]);

  // Cleanup: abort pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    tokens,
    loading,
    error,
    revokeToken,
    refreshTokens,
  };
}
